import { useMemo } from "react";
import { api, useLoad } from "../api";
import { ChartStack, type Level, type Marker, type PaneDef, type VMark } from "../chart/ChartStack";
import { Hint } from "../components/Hint";
import { UsRulesSwitch, usd, useUsRules } from "../components/UsRules";
import { fmt } from "../lib/format";
import { linkHandler } from "../lib/router";
import type { Num, UsAnalytics, UsMarketTrades, UsRulesKey, UsState } from "../types";

/**
 * Рынок США: графики позиций по отчётам CFTC за всю историю. Цен пока нет — сверху вместо свечей нетто-позиции групп.
 * Ось — отчёты (вторники); значение каждого отчёта известно с пятницы той же недели.
 */
export function UsMarketPage({ code }: { code: string }) {
  const res = useLoad((s) => api.usAnalytics(code, s), [code]);
  const d = res.data;
  const panes = useMemo(() => (d ? buildPanes(d) : []), [d]);
  const st = d?.state ?? null;

  return (
    <main className="page market us-market">
      <p className="crumbs">
        <a href="/us" onClick={linkHandler("/us")}>← Рынки США</a>
      </p>
      <section className="hero">
        <h1>
          {code} <span className="muted">{d?.name ?? ""}</span>
        </h1>
        <p className="lede">
          {d?.cftcCode?.startsWith("ICE:") ? (
            <>
              ICE Futures Europe, отчёт Disaggregated (только фьючерсы, с 2011 г.). Группы как у CFTC Legacy рассчитаны из
              него: коммерсанты = производители + своп-дилеры, крупные спекулянты = управляющие фонды + прочие.
            </>
          ) : (
            <>CFTC, код {d?.cftcCode ?? "—"}, отчёт Legacy (только фьючерсы).</>
          )}{" "}
          Цена — Yahoo Finance (ближайший контракт без склейки, прототип). Сделки и стопы по правилам США — на дневном
          графике ниже.
        </p>
        {st ? (
          <p className="asof">
            Отчёт на {fmt.date(st.date)}, опубликован {fmt.date(st.known)}{st.close != null ? `, цена недели ${fmt.price(st.close)}` : ""}. История с {fmt.date(d!.dates[0] ?? null)},{" "}
            {fmt.int(d!.dates.length)} отчётов.
          </p>
        ) : null}
      </section>

      {res.error ? <p className="form-error" role="alert">{res.error}</p> : null}
      {res.loading && !d ? <div className="skeleton" aria-busy="true">Загружаем…</div> : null}
      {d && !d.dates.length ? <p className="muted">Отчёты по этому рынку ещё не загружены.</p> : null}

      {st ? <StateFacts st={st} /> : null}

      {d && d.dates.length && d.price ? <TradesBlock code={code} /> : null}

      {d && d.dates.length ? (
        <div className="chart-card">
          <ChartStack dates={d.dates} panes={panes} initialBars={260} />
          <div className="chart-foot">
            <p className="chart-help">
              <kbd>Колесо</kbd> масштаб · <kbd>Перетаскивание</kbd> сдвиг · <kbd>Двойной клик</kbd> сброс. Один бар — один
              отчёт; до начала 1990-х CFTC публиковал отчёт дважды в месяц, окна индексов считаются в отчётах.
            </p>
            <details className="pane-help chart-legend-help">
              <summary>Что значат стрелки</summary>
              <div className="pane-help-body">
                <ul>
                  <li>
                    <b>На цене</b> — начало сетапа: коммерсанты в зоне (COT 26 ≥ 80 или WILLCO ≥ 75 для покупки, зеркально
                    для продажи), тренд по SMA 39 разрешает эту сторону{d?.category === "indices" ? "" : ", стохастик OI в нужной стороне"}.
                    Это сетап в адаптации Мосбиржи; входы и стопы по правилам США — на дневном графике выше.
                  </li>
                  <li>
                    <b>На нетто-позициях</b> — события только по позициям, без цены: зелёный ▲ / красный ▼ — началась
                    позиционная часть сетапа (коммерсанты в зоне{d?.category === "indices" ? "" : " и OI"}), либо нетто
                    коммерсантов дошло до максимума / минимума минимум за 3 года. Стрелка ставится в неделю, когда условие
                    появилось; если то же условие уже было в ту же сторону в последние {MARKER_GAP_WEEKS} недели,
                    новая стрелка не рисуется (только на графике — в таблице событий и в расчётах все события).
                  </li>
                  <li>Наведите курсор на стрелку — появится, что именно произошло. Полный список — в таблице событий ниже.</li>
                </ul>
              </div>
            </details>
          </div>
        </div>
      ) : null}

      {d && d.events.length ? (
        <section className="us-events">
          <h2>События по позициям</h2>
          <p className="lede">
            Последние 40 из {fmt.int(d.events.length)}. «Известно с» — пятница публикации: раньше этой даты сигнал
            использовать нельзя.
          </p>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="text">Отчёт</th>
                  <th className="text">Известно с</th>
                  <th className="text">Сторона</th>
                  <th className="text">Событие</th>
                </tr>
              </thead>
              <tbody>
                {d.events.slice(-40).reverse().map((e, i) => (
                  <tr key={`${e.date}-${e.code}-${i}`}>
                    <td className="text">{fmt.date(e.date)}</td>
                    <td className="text">{fmt.date(e.known)}</td>
                    <td className={`text ${e.direction > 0 ? "up" : e.direction < 0 ? "down" : ""}`}>
                      {e.direction > 0 ? "покупка" : e.direction < 0 ? "продажа" : "—"}
                    </td>
                    <td className="text">{e.title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </main>
  );
}

function StateFacts({ st }: { st: UsState }) {
  const side = (v: number) => (v > 0 ? "за покупку" : v < 0 ? "за продажу" : "нейтрально");
  const zone = (v: number) => (v > 0 ? "bull" : v < 0 ? "bear" : undefined);
  return (
    <dl className="facts">
      <div className={`fact fact-wide fact-primary${st.setupCore > 0 ? " fact-accent fact-bull" : st.setupCore < 0 ? " fact-accent fact-bear" : ""}`}>
        <dt>Сетап (позиции + тренд)</dt>
        <dd>{st.setupCore > 0 ? "на покупку" : st.setupCore < 0 ? "на продажу" : "нет"}</dd>
        <dd className="note">
          позиционная часть: {st.positionalCore > 0 ? "за покупку" : st.positionalCore < 0 ? "за продажу" : "нет"}
          {st.oiUsed ? "" : " (OI у индексов не учитывается)"}; тренд SMA 39: {trendText(st.trend)}
        </dd>
      </div>
      <Fact label="COT коммерсантов 26 / 156" value={`${fmt.idx(st.commCotShort)} / ${fmt.idx(st.commCotLong)}`} zone={zone(st.commDir)} note={side(st.commDir)} />
      <Fact label="WILLCO 26" value={fmt.idx(st.willco)} note={`нетто ${fmt.signed(st.commNet)}`} />
      <Fact
        label="Стохастик OI 52"
        value={fmt.idx(st.oiStoch)}
        zone={st.oiUsed ? zone(st.oiDir) : undefined}
        note={st.oiUsed ? `открыто ${fmt.int(st.openInterest)} контр.` : "у индексов не участвует"}
      />
      <Fact label="Мелкие трейдеры, COT 26" value={fmt.idx(st.crowdCotShort)} zone={zone(st.crowdDir)} note={`публика против: ${side(st.crowdDir)}`} />
      <Fact
        label="Экстремум нетто коммерсантов"
        value={st.recordYears === 0 ? "нет" : `${st.recordYears > 0 ? "максимум" : "минимум"} за ${Math.abs(st.recordYears)} г.`}
        zone={Math.abs(st.recordYears) >= 3 ? zone(st.recordYears) : undefined}
        note="в книге ориентир — 4 года"
      />
    </dl>
  );
}

export function trendText(t: number) {
  return t === 1 ? "вверх — покупки" : t === -1 ? "вниз — продажи" : t === 2 ? "плоский — обе стороны" : "нет цены";
}

function Fact({ label, value, note, zone }: { label: string; value: string; note?: string; zone?: string }) {
  return (
    <div className="fact">
      <dt>{label}</dt>
      <dd className={zone ? `zone-${zone}` : undefined}>{value}</dd>
      {note ? <dd className="note">{note}</dd> : null}
    </div>
  );
}

function lvl(y: number, className = "level-solid"): Level {
  return { y, className, label: String(y) };
}

const ZONE_BANDS = [
  { from: 80, to: 100, className: "band-bull" },
  { from: 0, to: 20, className: "band-bear" },
];

function buildPanes(d: UsAnalytics): PaneDef[] {
  const g = Object.fromEntries(d.groups.map((x) => [x.key, x]));
  const idxOf = new Map(d.dates.map((x, i) => [x, i]));
  const toMarkers = (codes: string[]): Marker[] =>
    thin(
      d.events
        .filter((e) => codes.includes(e.code))
        .map((e) => ({ index: idxOf.get(e.date) ?? -1, direction: e.direction, title: `${fmt.date(e.date)}: ${e.title}`, code: e.code }))
        .filter((m) => m.index >= 0),
    );
  const markers = toMarkers(["CORE", "RECORD"]);
  const pricePane: PaneDef[] = d.price
    ? [
        {
          id: "price",
          title: "Цена, неделя",
          hint: "Yahoo Finance, ближайший контракт без склейки: на смене контракта возможен ложный гэп",
          height: 260,
          markers: toMarkers(["SETUP"]),
          series: [
            { kind: "ohlc", key: "p", label: "Цена", open: d.price.open, high: d.price.high, low: d.price.low, close: d.price.close, up: "var(--bull)", down: "var(--bear)" },
            { kind: "line", key: "sma39", label: "SMA 39 (тренд)", color: "var(--ink-2)", values: d.price.sma39, width: 1.3, dash: "2 3" },
            { kind: "line", key: "sma52", label: "SMA 52", color: "var(--ink-3)", values: d.price.sma52, width: 1.2, dash: "5 4" },
          ],
          format: fmt.price as (v: number) => string,
        },
      ]
    : [];
  const panes: PaneDef[] = [
    ...pricePane,
    {
      id: "net",
      title: "Нетто-позиции групп",
      hint: "лонги минус шорты, контрактов",
      height: 220,
      zero: true,
      markers,
      series: [
        { kind: "line", key: "comm", label: "Коммерсанты", color: "var(--smart)", values: g.comm?.net ?? [], width: 2 },
        { kind: "line", key: "spec", label: "Крупные спекулянты", color: "var(--ink-2)", values: g.spec?.net ?? [], width: 1.3 },
        { kind: "line", key: "crowd", label: "Мелкие трейдеры", color: "var(--crowd)", values: g.crowd?.net ?? [], width: 1.2, dash: "2 3" },
      ],
      format: fmt.compact as (v: number) => string,
    },
    {
      id: "cot",
      title: "COT Index",
      hint: "положение нетто в диапазоне 26 и 156 отчётов, 0–100",
      height: 150,
      fixed: [0, 100],
      bands: ZONE_BANDS,
      levels: [lvl(80, "level-bull"), lvl(20, "level-bear"), { y: 50, className: "level-mid", label: "50" }],
      series: [
        { kind: "line", key: "cs", label: "Коммерсанты, 26", color: "var(--smart)", values: g.comm?.cotShort ?? [], width: 2 },
        { kind: "line", key: "cl", label: "Коммерсанты, 156", color: "var(--smart)", values: g.comm?.cotLong ?? [], width: 1.2, dash: "4 4" },
        { kind: "line", key: "ps", label: "Мелкие, 26 (контр-индикатор)", color: "var(--crowd)", values: g.crowd?.cotShort ?? [], width: 1.2, dash: "2 3" },
      ],
    },
    {
      id: "willco",
      title: "WILLCO",
      hint: "нетто коммерсантов к открытому интересу, стохастик 26",
      height: 120,
      fixed: [0, 100],
      bands: [
        { from: 75, to: 100, className: "band-bull" },
        { from: 0, to: 25, className: "band-bear" },
      ],
      levels: [lvl(75, "level-bull"), lvl(25, "level-bear")],
      series: [{ kind: "line", key: "w", label: "WILLCO", color: "var(--smart)", values: d.willco ?? [], width: 2 }],
    },
    {
      id: "oi",
      title: "Открытый интерес",
      hint: "контрактов; пунктир — нетто коммерсантов на своей шкале",
      height: 140,
      series: [
        { kind: "line", key: "oi", label: "OI", color: "var(--ink-2)", values: d.openInterest ?? [], width: 1.6 },
        { kind: "line", key: "oin", label: "Нетто коммерсантов (своя шкала)", color: "var(--smart)", values: g.comm?.net ?? [], width: 1.1, dash: "4 3", axis: "right" },
      ],
      format: fmt.compact as (v: number) => string,
    },
    {
      id: "oiSt",
      title: "Стохастик OI, 52 отчёта",
      hint: d.category === "indices" ? "у индексов искажён экспирациями — в сетап не входит" : "низкий OI и покупки коммерсантов — лучшие покупки",
      height: 110,
      fixed: [0, 100],
      bands: ZONE_BANDS,
      levels: [lvl(80, "level-bull"), lvl(20, "level-bear")],
      series: [{ kind: "line", key: "ois", label: "Стохастик OI", color: "var(--ink-2)", values: d.oiStoch ?? [], width: 1.6 }],
    },
  ];
  if (d.extras.length) {
    const report = d.extras[0].report;
    const colors = ["var(--smart)", "var(--ink-2)", "var(--crowd)"];
    panes.push({
      id: "extra",
      title: report === "TFF" ? "TFF: дилеры, управляющие, фонды с плечом" : "Disaggregated: производители, своп-дилеры, фонды",
      hint: "нетто, контрактов; отчёт с 2006 г.",
      height: 170,
      zero: true,
      series: d.extras.map((x, i) => ({
        kind: "line" as const,
        key: x.key,
        label: x.label,
        color: colors[i % colors.length],
        values: x.net as Num[],
        width: i === 0 ? 1.8 : 1.2,
        dash: i === 2 ? "2 3" : undefined,
      })),
      format: fmt.compact as (v: number) => string,
    });
  }
  return panes;
}

/** Новая стрелка того же события в ту же сторону — не раньше чем через столько недель (только отображение). */
export const MARKER_GAP_WEEKS = 4;

/**
 * Стрелки событий шли гроздьями, когда условие пропадало и снова появлялось. Для графика: событие с тем же кодом и
 * стороной рисуется, только если его не было в предыдущие MARKER_GAP_WEEKS отчётов (расчёты и таблица — без изменений).
 */
export function thin(ms: (Marker & { code: string })[]): Marker[] {
  const last = new Map<string, number>();
  const out: Marker[] = [];
  for (const m of [...ms].sort((a, b) => a.index - b.index)) {
    const key = `${m.code}:${m.direction}`;
    const prev = last.get(key);
    last.set(key, m.index);
    if (prev != null && m.index - prev <= MARKER_GAP_WEEKS) continue;
    out.push({ index: m.index, direction: m.direction, title: m.title });
  }
  return out;
}

function isoMinusYears(years: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().slice(0, 10);
}

/** Дневной график: цена, стоп открытой позиции, входы и выходы по правилам США, отметки дат публикации отчётов. */
function TradesBlock({ code }: { code: string }) {
  const [rules, setRules] = useUsRules();
  const from = useMemo(() => isoMinusYears(3), []);
  const res = useLoad((s) => api.usMarketTrades(code, rules, from, s), [code, rules, from]);
  const t = res.data;
  const chart = useMemo(() => (t ? buildDaily(t, rules) : null), [t, rules]);
  return (
    <section className="chart-card">
      <h2>
        Сделки и стопы, дневной график <Hint>Цена — Yahoo, ближайший контракт без склейки. Зелёный ▲ / красный ▼ — вход в покупку / продажу; бледная стрелка — выход. Пунктир — стоп открытой позиции. Вертикальные отметки — даты публикации отчётов CFTC: сделка по отчёту возможна только после такой отметки.</Hint>
      </h2>
      <UsRulesSwitch rules={rules} onChange={setRules} />
      {res.error ? <p className="form-error" role="alert">{res.error}</p> : null}
      {res.loading && !t ? <div className="skeleton" aria-busy="true">Загружаем…</div> : null}
      {t && chart ? (
        <>
          {t.open ? (
            <p className="asof">
              Открыта {t.open.dir > 0 ? "покупка" : "продажа"} с {fmt.date(t.open.entry)} по {fmt.price(t.open.entryPrice)}
              {t.open.report ? ` (отчёт ${fmt.date(t.open.report)}, опубликован ${fmt.date(t.open.published)})` : " (перевход)"}
              ; стоп {fmt.price(t.days.at(-1)?.stop ?? null)}.
            </p>
          ) : (
            <p className="asof">Открытой позиции нет.</p>
          )}
          <ChartStack dates={chart.dates} panes={chart.panes} vmarks={chart.vmarks} initialBars={260} />
          <TradeList t={t} />
        </>
      ) : null}
    </section>
  );
}

function buildDaily(t: UsMarketTrades, rules: UsRulesKey) {
  const dates = t.days.map((x) => x.date);
  const idx = new Map(dates.map((x, i) => [x, i]));
  const firstOnOrAfter = (iso: string) => {
    let lo = 0;
    let hi = dates.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (dates[mid] < iso) lo = mid + 1;
      else hi = mid;
    }
    return lo < dates.length ? lo : -1;
  };
  const markers: Marker[] = [];
  const all = t.open ? [...t.trades, t.open] : t.trades;
  for (const x of all) {
    const i = idx.get(x.entry);
    if (i != null)
      markers.push({
        index: i,
        direction: x.dir,
        title: `${fmt.date(x.entry)}: вход в ${x.dir > 0 ? "покупку" : "продажу"} по ${fmt.price(x.entryPrice)}${x.reentry ? " (перевход)" : ""} [${rules}]`,
      });
    if (x.exit) {
      const j = idx.get(x.exit);
      if (j != null)
        markers.push({ index: j, direction: -x.dir, muted: true, title: `${fmt.date(x.exit)}: выход (${x.reason}), итог ${usd(x.netUsd)}` });
    }
  }
  const vmarks: VMark[] = [];
  for (const [report, published] of t.publications) {
    const i = firstOnOrAfter(published);
    if (i >= 0) vmarks.push({ index: i, title: `Отчёт ${fmt.date(report)} опубликован ${fmt.date(published)}`, className: "vmark-pub" });
  }
  const panes: PaneDef[] = [
    {
      id: "dprice",
      title: "Цена, день",
      hint: "входы, выходы и стоп по выбранным правилам",
      height: 300,
      markers,
      series: [
        {
          kind: "ohlc",
          key: "p",
          label: "Цена",
          open: t.days.map((x) => x.open),
          high: t.days.map((x) => x.high),
          low: t.days.map((x) => x.low),
          close: t.days.map((x) => x.close),
          up: "var(--bull)",
          down: "var(--bear)",
        },
        { kind: "line", key: "stop", label: "Стоп", color: "var(--bear)", values: t.days.map((x) => x.stop), width: 1.4, step: true, dash: "1 2", noCarry: true, emptyText: "нет позиции" },
      ],
      format: fmt.price as (v: number) => string,
    },
  ];
  return { dates, panes, vmarks };
}

function TradeList({ t }: { t: UsMarketTrades }) {
  const rows = [...t.trades].reverse().slice(0, 20);
  if (!rows.length) return null;
  return (
    <details className="pane-help">
      <summary>Сделки за три года ({t.trades.length})</summary>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th className="text">Сторона</th>
              <th className="text">Вход</th>
              <th className="text">Выход</th>
              <th className="text">Причина</th>
              <th>Итог, $</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x, i) => (
              <tr key={`${x.entry}-${i}`}>
                <td className={`text ${x.dir > 0 ? "up" : "down"}`}>{x.dir > 0 ? "покупка" : "продажа"}{x.reentry ? " (перевход)" : ""}</td>
                <td className="text">{fmt.date(x.entry)} · {fmt.price(x.entryPrice)}</td>
                <td className="text">{fmt.date(x.exit)}</td>
                <td className="text">{x.reason}</td>
                <td className={x.netUsd > 0 ? "up" : x.netUsd < 0 ? "down" : undefined}>{usd(x.netUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
